import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { ArrowRight, KeyRound } from "lucide-react";
import { atlasFetch, setStoredKey, clearStoredKey, getStoredKey, keyMode, ATLAS_BASE } from "@/lib/atlas-api";

function ApiHealthBadge() {
  const { t } = useTranslation("portal");
  const [status, setStatus] = useState("checking");
  const [latency, setLatency] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const ping = async () => {
      const t0 = performance.now();
      try {
        const r = await fetch(`${ATLAS_BASE}/api/v1/health`, {
          method: "GET",
          cache: "no-store",
        });
        const ms = Math.round(performance.now() - t0);
        if (cancelled) return;
        if (r.ok) {
          setStatus("ok");
          setLatency(ms);
        } else {
          setStatus("down");
          setLatency(ms);
        }
      } catch {
        if (cancelled) return;
        setStatus("down");
        setLatency(null);
      }
    };
    ping();
    return () => { cancelled = true; };
  }, []);

  const palette =
    status === "ok"
      ? { dot: "bg-emerald-500", bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" }
      : status === "down"
      ? { dot: "bg-rose-500", bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200" }
      : { dot: "bg-slate-400", bg: "bg-slate-50", text: "text-slate-600", border: "border-slate-200" };

  const label =
    status === "ok" ? `${t("login.healthOk")}${latency != null ? ` · ${latency}ms` : ""}`
    : status === "down" ? t("login.networkError")
    : "…";

  return (
    <span
      data-testid="portal-api-health-badge"
      data-status={status}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold border ${palette.bg} ${palette.text} ${palette.border}`}
      title={`${ATLAS_BASE}/api/v1/health`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${palette.dot} ${status === "checking" ? "animate-pulse" : ""}`} />
      {label}
    </span>
  );
}

export default function PortalLogin() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("portal");
  const [key, setKey] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Portal routes live outside the /:lang/* tree, so LanguageSync never
  // fires. Read the user's persisted choice on mount so the portal UI
  // shows in the language they picked on the marketing site.
  useEffect(() => {
    try {
      const stored = localStorage.getItem("atlasforge_lang");
      if (stored && stored !== i18n.language) {
        i18n.changeLanguage(stored);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Already signed in? Skip the form.
  useEffect(() => {
    if (getStoredKey()) navigate("/portal", { replace: true });
  }, [navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const trimmed = key.trim();
    if (!trimmed) return;
    if (!trimmed.startsWith("afg_live_") && !trimmed.startsWith("afg_test_")) {
      toast.error(t("login.invalidKey"));
      return;
    }
    setSubmitting(true);
    // Stage the key so atlasFetch can read it; clear it on any failure.
    setStoredKey(trimmed);
    try {
      const me = await atlasFetch("/api/v1/me");
      const mode = keyMode(trimmed);
      toast.success(`Welcome, ${me.company || me.name} · ${mode === "production" ? "PRODUCTION" : "SANDBOX"}`);
      navigate("/portal", { replace: true });
    } catch (err) {
      clearStoredKey();
      if (err.status === 0) {
        toast.error(t("login.networkError"));
      } else if (err.status === 401) {
        toast.error(t("login.invalidKey"));
      } else {
        toast.error(err.message || t("login.invalidKey"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="min-h-screen bg-[#F5F1EB]/60 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <a href="/" className="font-display text-[22px] text-[#1A4232]">
            AtlasForge<span className="text-[#0A1128]"> Financial</span>
          </a>
          <p className="mt-1 text-[12px] uppercase tracking-[0.18em] text-slate-500">{t("login.title")}</p>
        </div>

        <div
          data-testid="portal-login-card"
          className="rounded-[14px] bg-white border border-slate-200 shadow-[0_2px_24px_-12px_rgba(15,23,42,.15)] p-8"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-[10px] bg-[#1A4232] text-[#F5F1EB]">
                <KeyRound className="h-5 w-5" />
              </span>
              <div>
                <h1 className="font-display text-[22px] text-[#0A1128] leading-tight">{t("login.signIn")}</h1>
                <p className="text-[13px] text-slate-500">{t("login.subtitle")}</p>
              </div>
            </div>
            <ApiHealthBadge />
          </div>

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <label className="text-[12px] font-medium text-slate-700">{t("login.apiKeyLabel")}</label>
              <input
                autoFocus
                data-testid="portal-login-key-input"
                type="password"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder={t("login.apiKeyPlaceholder")}
                className="mt-1.5 w-full rounded-[10px] border border-slate-300 px-3.5 py-2.5 text-[14px] font-mono tracking-tight outline-none focus:border-[#1A4232] focus:ring-2 focus:ring-[#1A4232]/15"
              />
              <p className="mt-2 text-[12px] text-slate-500">
                <code className="font-mono">afg_live_…</code> → PRODUCTION ·{" "}
                <code className="font-mono">afg_test_…</code> → SANDBOX
              </p>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="portal-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1A4232] text-[#F5F1EB] text-[14px] font-medium px-5 py-2.5 hover:bg-[#0E2A20] transition-colors disabled:opacity-60"
            >
              {submitting ? t("login.signingIn") : t("login.signIn")}
              {!submitting && <ArrowRight className="h-4 w-4" />}
            </button>
          </form>

          <div className="mt-6 pt-5 border-t border-slate-100 text-[12.5px] text-slate-500">
            {t("login.helpTitle")}{" "}
            <a
              href="mailto:customersupport@atlasforgefinancial.com"
              className="text-[#1A4232] underline"
            >
              customersupport@atlasforgefinancial.com
            </a>
            {" · "}
            <a
              href={`${ATLAS_BASE}/api/v1/integration-guide`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#1A4232] underline"
            >
              {t("login.helpDocsLabel")}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
